import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from '@clarix/ui';
import { isConnectivityLost, logger } from '@clarix/utils';
import { AudioWorkletRecorder, resumeContext, stopMediaRecorder, watchRecorderData } from './audio-worklet-recorder';
import { RecordingDB } from './recording-db';
import { ChunkUploadQueue } from './chunk-upload-queue';
import { isNetworkFailure, isStorageUnreachable, uploadChunkToS3 } from './s3-upload';
import { uploadRecordingFile, type FileUploadProgress } from './file-upload';
import { _useRecordingInternals, type RecordingEngineRefs } from './RecordingProvider';
import { LiveInput, MicSwitchError, openMic, stopStream } from './mic-input';
import type {
  ActiveMic,
  CaptureFailure,
  MicChoice,
  MicSwapEvent,
  MicSwitchReason,
  RecordingAdapter,
  RecordingEngineConfig,
  RecordingInputEvent,
  RecordingStartOptions,
} from './types';
import type { ClaraRecordingStatus, ClaraUploadState, PresignedUrlItem } from '@clarix/shared-types';
import {
  CHUNK_UPLOAD_FAILED_MESSAGE,
  HIDE_FLUSH_MIN_INTERVAL_MS,
  IDB_FIRST_PERSIST_THRESHOLD_BYTES,
  IDB_PERSIST_THRESHOLD_BYTES,
  NETWORK_MESSAGE,
  RECORDER_ERROR_EMPTY_MESSAGE,
  RECORDER_ERROR_MESSAGE,
  RECORDER_STALLED_EMPTY_MESSAGE,
  RECORDER_STALLED_MESSAGE,
  RECORDING_STOP_FAILED_MESSAGE,
  RECORDING_UPLOADED_MESSAGE,
  SAVE_REFUSED_MESSAGE,
  UPLOAD_UNREACHABLE_MESSAGE,
  UPLOAD_MAX_RETRIES,
  UPLOAD_RETRY_DELAYS_MS,
} from './constants';

// ── Minimum S3 part size (5 MB). Only the final part may be smaller. ───────
const MIN_PART_SIZE = 5 * 1024 * 1024;

// ── MediaRecorder fallback constants ────────────────────────────────────────
const URL_REFILL_THRESHOLD = 3;

function getSupportedMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return '';
}

type InputRefs = Pick<RecordingEngineRefs, 'statusRef' | 'liveInputRef' | 'inputListenersRef'>;

/** `starting` included: a mic unplugged between attach and the first chunk must still be replaced. */
const CAPTURING = new Set(['starting', 'recording', 'paused']);

function emitInput(refs: InputRefs, event: RecordingInputEvent): void {
  refs.inputListenersRef.current.forEach((listener) => {
    // A consumer's toast code must never take the recording down with it.
    try {
      listener(event);
    } catch (err) {
      logger.error('[Recording] input event listener threw', err);
    }
  });
}

/**
 * Module-level rather than a hook callback: an unplugged mic must be replaced even after the
 * component that started the recording has unmounted (the provider outlives it).
 */
async function switchEngineInput(
  refs: InputRefs,
  choice: MicChoice | null,
  reason: MicSwitchReason,
): Promise<ActiveMic> {
  if (!CAPTURING.has(refs.statusRef.current)) {
    throw new MicSwitchError('NOT_RECORDING', 'Nothing is being recorded');
  }
  const input = refs.liveInputRef.current;
  if (!input) {
    throw new MicSwitchError('UNSUPPORTED', "This browser can't change the microphone during a recording");
  }
  try {
    const { mic, swap } = await input.switchTo(choice, reason);
    if (swap) emitInput(refs, { type: 'switched', mic, swap });
    return mic;
  } catch (err) {
    const error =
      err instanceof MicSwitchError ? err : new MicSwitchError('OPEN_FAILED', "Couldn't switch the microphone", err);
    // A switch overtaken by stop/abort is not news: the take is already finished.
    if (error.code !== 'NOT_RECORDING' && CAPTURING.has(refs.statusRef.current)) {
      const mic = input.mic;
      emitInput(refs, { type: 'switch-failed', target: choice, reason, error, mic });
      if (!mic) emitInput(refs, { type: 'input-lost', error });
    }
    throw error;
  }
}

/** A graph the fallback engine can swap mics in; null where Web Audio can't run unprompted. */
async function startFallbackGraph(): Promise<AudioContext | null> {
  if (typeof AudioContext === 'undefined') return null;
  const context = new AudioContext();
  await resumeContext(context);
  if (context.state === 'running') return context;
  void context.close();
  return null;
}

/** How `adapter.complete` answered. `rejected`: it threw (projects, pms), so the engine says why. */
interface CompleteAnswer {
  accepted: boolean;
  rejected: boolean;
  /** Projects' "no transcript" answer: the old quiet ending, but the device copy is kept. */
  noTranscript: boolean;
}

/** What a finished stop tells the user. `saved`: audio reached the server and it took the recording. */
function announceStop(failure: CaptureFailure | undefined, saved: boolean, autoStopped: boolean): void {
  if (failure === 'stalled') toast.warning(saved ? RECORDER_STALLED_MESSAGE : RECORDER_STALLED_EMPTY_MESSAGE);
  else if (failure === 'error') toast.warning(saved ? RECORDER_ERROR_MESSAGE : RECORDER_ERROR_EMPTY_MESSAGE);
  else if (saved && !autoStopped) toast.success(RECORDING_UPLOADED_MESSAGE);
}

const EMPTY_BLOB = new Blob([]);

/**
 * The fallback engine keeps its parts in memory; at its end they go to IndexedDB like the worklet's:
 * the uploaded parts as `done` records (no audio, just the ETag) and the unsent buffer as `queued`.
 */
async function persistFallbackParts(
  db: RecordingDB,
  scopeId: string,
  state: ClaraUploadState,
  buffer: Blob[],
): Promise<void> {
  for (const part of state.completedParts) {
    const id = await db.savePart(scopeId, part.part_number, EMPTY_BLOB);
    await db.markDone(id, part.etag);
  }
  if (buffer.length > 0) await db.savePart(scopeId, state.nextPartNumber++, new Blob(buffer));
}

/** True while a track can still deliver audio; the fallback without a graph reads its raw stream. */
function streamDelivering(stream: MediaStream | null): boolean {
  const track = stream?.getAudioTracks()[0];
  return !!track && track.readyState === 'live' && !track.muted;
}

export interface UseRecordingControlsReturn {
  start: (scopeId: string, filename: string, options?: RecordingStartOptions) => Promise<void>;
  /**
   * Swap the mic mid-recording into the same file; `null` = the OS default. Rejects with a
   * `MicSwitchError` and keeps the current mic when the new one can't be opened.
   */
  switchMic: (choice: MicChoice | null, reason?: MicSwitchReason) => Promise<ActiveMic>;
  /** The mic being recorded right now, from the live track; null when not capturing. */
  getActiveMic: () => ActiveMic | null;
  /** Level tap on the live mic inside the recording graph — a meter needs no second capture. */
  getAnalyser: () => AnalyserNode | null;
  /** The live mic stream; it changes on every switch. */
  getStream: () => MediaStream | null;
  stop: () => Promise<void>;
  /** Ends capture and keeps every byte in IndexedDB: nothing is uploaded, completed or aborted. */
  halt: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  abort: () => Promise<void>;
  /**
   * Upload an already-recorded audio file instead of capturing live.
   *
   * Shares this hook's status machine so every consumer — chips, banners,
   * polling — reacts the same way it does to a live recording. Resolves once
   * the backend has accepted the upload; the AI pipeline runs on after that,
   * exactly as it does for `stop()`.
   */
  uploadFile: (
    scopeId: string,
    file: File,
    onProgress?: (progress: FileUploadProgress) => void,
  ) => Promise<void>;
  status: ClaraRecordingStatus;
  isProcessing: boolean;
}

/**
 * Generic recording control hook. Takes an app-supplied adapter (which binds the
 * app-specific scope, e.g. meetingId or participantId, to the backend endpoints)
 * and a config. Returns start/stop/pause/resume/abort and the current status.
 *
 * Must be rendered inside a RecordingProvider.
 */
export function useRecordingControls(
  adapter: RecordingAdapter,
  config: RecordingEngineConfig,
): UseRecordingControlsReturn {
  const ctx = _useRecordingInternals();
  const {
    engineRefs,
    activeScopeId,
    setActiveScopeId,
    status,
    isProcessing,
    setStatus,
    setError,
    setIsProcessing,
    setWasAutoStopped,
    setActiveJobId,
  } = ctx;

  const {
    statusRef,
    uploadStateRef,
    autoStopTimerRef,
    segmentStartTimeRef,
    maxDurationMsRef,
    remainingMsRef,
    autoStoppedRef,
    stopAtTimeRef,
    isStoppingRef,
    triggerAutoStopRef,
    engineTypeRef,
    workletRecorderRef,
    recordingDbRef,
    uploadQueueRef,
    wavAccRef,
    wavAccSizeRef,
    s3PartNumberRef,
    pendingChunkRef,
    mediaRecorderRef,
    streamRef,
    bufferRef,
    bufferSizeRef,
    isUploadingRef,
    isFetchingMoreUrlsRef,
    liveInputRef,
    inputContextRef,
    inputListenersRef,
    flushTailRef,
  } = engineRefs;

  // Subscribed once per mounted caller; the latest handler is read at emit time.
  const onInputEvent = config.onInputEvent;
  const onInputEventRef = useRef(onInputEvent);
  useEffect(() => {
    onInputEventRef.current = onInputEvent;
  });
  useEffect(() => {
    const listeners = inputListenersRef.current;
    const listener = (event: RecordingInputEvent) => onInputEventRef.current?.(event);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [inputListenersRef]);

  // ── Shared helpers ─────────────────────────────────────────────────

  const updateStatus = useCallback(
    (newStatus: ClaraRecordingStatus) => {
      statusRef.current = newStatus;
      setStatus(newStatus);
    },
    [statusRef, setStatus],
  );

  const clearAutoStopTimer = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, [autoStopTimerRef]);

  const releaseStream = useCallback(() => {
    liveInputRef.current?.stop();
    liveInputRef.current = null;
    void inputContextRef.current?.close();
    inputContextRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, [streamRef, liveInputRef, inputContextRef]);

  /** Replace an unplugged mic with the OS default; the take carries on either way. */
  const handleInputEnded = useCallback(() => {
    void switchEngineInput({ statusRef, liveInputRef, inputListenersRef }, null, 'disconnected').catch(
      () => undefined,
    );
  }, [statusRef, liveInputRef, inputListenersRef]);

  /** A stop that cannot run says so, so no caller waits on a save that never happens. */
  const reportStopFailed = useCallback(
    (message: string) => {
      logger.error(`[Recording] stop failed: ${message}`);
      setError(message);
      updateStatus('error');
      if (!isConnectivityLost()) toast.error(message);
    },
    [setError, updateStatus],
  );

  // The watchers outlive the render that started them, so they call the latest stop through this.
  const captureFailureRef = useRef<(scopeId: string, failure: CaptureFailure) => void>(() => {});

  // ── AudioWorklet: handle incoming PCM chunk ────────────────────────────

  /** Writes the audio gathered since the last block to IndexedDB as the next part number. */
  const persistAccumulated = useCallback(
    async (scopeId: string) => {
      const db = recordingDbRef.current;
      if (!db || wavAccRef.current.length === 0) return;
      const merged = new Blob(wavAccRef.current, { type: wavAccRef.current[0]?.type || 'audio/mp4' });
      wavAccRef.current = [];
      wavAccSizeRef.current = 0;
      const partNumber = s3PartNumberRef.current++;
      try {
        await db.savePart(scopeId, partNumber, merged);
      } catch (err) {
        // Back in memory for the next write: a failed write must never drop the audio.
        wavAccRef.current.unshift(merged);
        wavAccSizeRef.current += merged.size;
        throw err;
      }
      uploadQueueRef.current?.notify();
    },
    [recordingDbRef, uploadQueueRef, wavAccRef, wavAccSizeRef, s3PartNumberRef],
  );

  /** Queued behind earlier chunks; the chain survives a failed write, which keeps its audio in memory. */
  const queuePersist = useCallback(
    (scopeId: string) => {
      pendingChunkRef.current = pendingChunkRef.current
        .then(() => persistAccumulated(scopeId))
        .catch((err: unknown) => logger.error('[Recording] could not save audio to IndexedDB', err));
    },
    [pendingChunkRef, persistAccumulated],
  );

  const lastHideFlushRef = useRef(0);
  /** A hidden or closing tab writes what is still in memory, then the recorder's newest fragment. */
  const flushTail = useCallback(
    (scopeId: string, final: boolean) => {
      if (!final && Date.now() - lastHideFlushRef.current < HIDE_FLUSH_MIN_INTERVAL_MS) return;
      lastHideFlushRef.current = Date.now();
      queuePersist(scopeId);
      void workletRecorderRef.current?.flush().then(() => queuePersist(scopeId));
    },
    [queuePersist, workletRecorderRef],
  );

  const handleWorkletChunk = useCallback(
    (blob: Blob, scopeId: string) => {
      const state = uploadStateRef.current;
      if (!state) return;

      pendingChunkRef.current = pendingChunkRef.current
        .then(async () => {
          wavAccRef.current.push(blob);
          wavAccSizeRef.current += blob.size;
          const threshold =
            s3PartNumberRef.current === 1 ? IDB_FIRST_PERSIST_THRESHOLD_BYTES : IDB_PERSIST_THRESHOLD_BYTES;
          if (wavAccSizeRef.current >= threshold) await persistAccumulated(scopeId);
        })
        .catch((err: unknown) => logger.error('[Recording] could not save audio to IndexedDB', err));

      // Wall-clock auto-stop, after the chunk is queued: its audio predates the cap.
      if (
        stopAtTimeRef.current > 0 &&
        Date.now() >= stopAtTimeRef.current &&
        !isStoppingRef.current &&
        (statusRef.current === 'recording' || statusRef.current === 'paused')
      ) {
        triggerAutoStopRef.current?.();
      }
    },
    [
      uploadStateRef,
      stopAtTimeRef,
      isStoppingRef,
      statusRef,
      triggerAutoStopRef,
      pendingChunkRef,
      wavAccRef,
      wavAccSizeRef,
      s3PartNumberRef,
      persistAccumulated,
    ],
  );

  // ── Backend completion ────────────────────────────────────────────────

  /** `adapter.complete`; only an accepted answer lets the device copy go. */
  const completeUpload = useCallback(
    async (
      uploadId: string,
      parts: { part_number: number; etag: string }[],
      durationSeconds: number,
      inputSwaps: MicSwapEvent[],
    ): Promise<CompleteAnswer> => {
      // The 202 means the AI work STARTED; only the WS `recording.ready` handler clears processing.
      setIsProcessing(true);
      try {
        const res = await adapter.complete(
          { upload_id: uploadId, parts, duration_seconds: durationSeconds },
          { input_swaps: inputSwaps },
        );
        if (res.job_id != null) setActiveJobId(res.job_id);
        if (res.accepted === false) setIsProcessing(false);
        return { accepted: res.accepted !== false, rejected: false, noTranscript: false };
      } catch (err) {
        logger.error('[Recording] completeUpload failed:', err);
        setIsProcessing(false);
        const backendCode = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
        return { accepted: false, rejected: true, noTranscript: backendCode === 'NO_TRANSCRIPT' };
      }
    },
    [adapter, setIsProcessing, setActiveJobId],
  );

  /**
   * Both engines end here once every byte is in IndexedDB: drain to S3, complete, and only then
   * clear the device's copy. A failure keeps IndexedDB and never aborts the server upload.
   */
  const finishFromDb = useCallback(
    async (
      scopeId: string,
      state: ClaraUploadState,
      db: RecordingDB,
      queue: ChunkUploadQueue,
      inputSwaps: MicSwapEvent[],
      failure: CaptureFailure | undefined,
    ) => {
      try {
        await queue.drain();
        const parts = await db.getCompletedParts(scopeId);
        const durationSeconds = Math.round((Date.now() - state.recordingStartTime) / 1000);
        const answer = await completeUpload(state.uploadId, parts, durationSeconds, inputSwaps);
        if (answer.accepted) {
          updateStatus('completed');
          await db.clearRecording(scopeId).catch((err: unknown) => logger.warn('[Recording] IndexedDB not cleared', err));
          announceStop(failure, parts.length > 0, autoStoppedRef.current);
        } else if (answer.noTranscript) {
          updateStatus('completed');
        } else {
          // Not taken, so nothing is cleared. An adapter that answered quietly shows its own state.
          setError(SAVE_REFUSED_MESSAGE);
          updateStatus('error');
          if (answer.rejected) {
            toast.error(parts.length > 0 || failure !== 'stalled' ? SAVE_REFUSED_MESSAGE : RECORDER_STALLED_EMPTY_MESSAGE);
          }
        }
      } catch (err) {
        const network = isNetworkFailure(err);
        const message = isStorageUnreachable(err)
          ? UPLOAD_UNREACHABLE_MESSAGE
          : network
            ? NETWORK_MESSAGE
            : err instanceof Error
              ? err.message
              : RECORDING_STOP_FAILED_MESSAGE;
        logger.error('[Recording] stop could not finish; the audio stays in IndexedDB', err);
        setError(message);
        updateStatus('error');
        if (!network) {
          toast.error(message);
          await adapter.markUploadFailed?.({ upload_id: state.uploadId }).catch(() => {});
        }
      } finally {
        db.close();
        if (recordingDbRef.current === db) recordingDbRef.current = null;
        if (uploadQueueRef.current === queue) uploadQueueRef.current = null;
      }
    },
    [adapter, completeUpload, setError, updateStatus, autoStoppedRef, recordingDbRef, uploadQueueRef],
  );

  // ── AudioWorklet: stop logic ───────────────────────────────────────────

  const performStopWorklet = useCallback(
    async (scopeId: string, failure?: CaptureFailure) => {
      const state = uploadStateRef.current;
      const db = recordingDbRef.current;
      const queue = uploadQueueRef.current;
      const worklet = workletRecorderRef.current;

      if (!state || !db || !queue) {
        reportStopFailed(RECORDING_STOP_FAILED_MESSAGE);
        return;
      }

      clearAutoStopTimer();
      flushTailRef.current = null;
      updateStatus('completing');
      // Processing covers the whole stop; WS `recording.ready` is the only place that turns it off.
      setIsProcessing(true);

      let inputSwaps: MicSwapEvent[] = [];
      try {
        // stop(), not flush(): MP4 hands over the audio after its last 5 s fragment only on stop,
        // and it must reach wavAccRef before the final part is cut below.
        await worklet?.stop();
        inputSwaps = liveInputRef.current?.swapEvents() ?? [];
        worklet?.destroy();
        workletRecorderRef.current = null;
        liveInputRef.current = null;
        await pendingChunkRef.current;
        await persistAccumulated(scopeId);
      } catch (err) {
        // What reached IndexedDB stays there for the caller's retry.
        db.close();
        recordingDbRef.current = null;
        uploadQueueRef.current = null;
        reportStopFailed(err instanceof Error ? err.message : RECORDING_STOP_FAILED_MESSAGE);
        return;
      }
      await finishFromDb(scopeId, state, db, queue, inputSwaps, failure);
    },
    [
      updateStatus,
      clearAutoStopTimer,
      finishFromDb,
      persistAccumulated,
      reportStopFailed,
      setIsProcessing,
      uploadStateRef,
      recordingDbRef,
      uploadQueueRef,
      workletRecorderRef,
      pendingChunkRef,
      liveInputRef,
      flushTailRef,
    ],
  );

  // ── MediaRecorder: upload helpers (fallback path) ─────────────────────

  const fetchMoreUrlsIfNeeded = useCallback(async () => {
    const state = uploadStateRef.current;
    if (!state || isFetchingMoreUrlsRef.current) return;
    if (state.presignedUrls.length >= URL_REFILL_THRESHOLD) return;

    isFetchingMoreUrlsRef.current = true;
    try {
      const response = await adapter.moreUrls({
        upload_id: state.uploadId,
        next_part_number: state.nextPartNumber,
      });
      state.presignedUrls.push(...response.presigned_urls);
    } catch (err) {
      logger.warn('Recording URL prefetch failed; next chunk will retry', err);
    } finally {
      isFetchingMoreUrlsRef.current = false;
    }
  }, [adapter, uploadStateRef, isFetchingMoreUrlsRef]);

  const uploadChunkWithRetry = useCallback(
    async (presignedUrl: string, chunk: Blob, partNumber: number): Promise<string> => {
      let lastError: unknown;
      for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
        try {
          return await uploadChunkToS3(presignedUrl, chunk);
        } catch (err) {
          lastError = err;
          if (attempt < UPLOAD_MAX_RETRIES) {
            const delay = UPLOAD_RETRY_DELAYS_MS[attempt - 1] ?? 4000;
            await new Promise((r) => setTimeout(r, delay));
          }
        }
      }
      logger.error(`[Recording] part ${partNumber} failed after ${UPLOAD_MAX_RETRIES} attempts`);
      throw lastError;
    },
    [],
  );

  const processBuffer = useCallback(async () => {
    const state = uploadStateRef.current;
    if (!state || isUploadingRef.current) return;
    if (bufferSizeRef.current < MIN_PART_SIZE) return;

    isUploadingRef.current = true;
    try {
      while (bufferSizeRef.current >= MIN_PART_SIZE) {
        const fullBlob = new Blob(bufferRef.current);
        const chunkBlob = fullBlob.slice(0, state.partSizeBytes);
        const remainderBlob = fullBlob.slice(state.partSizeBytes);

        bufferRef.current = remainderBlob.size > 0 ? [remainderBlob] : [];
        bufferSizeRef.current = remainderBlob.size;

        try {
          if (state.presignedUrls.length === 0) {
            const response = await adapter.moreUrls({
              upload_id: state.uploadId,
              next_part_number: state.nextPartNumber,
            });
            state.presignedUrls.push(...response.presigned_urls);
          }

          const urlItem = state.presignedUrls.shift() as PresignedUrlItem;
          const partNumber = urlItem.part_number;
          state.nextPartNumber = partNumber + 1;

          const etag = await uploadChunkWithRetry(urlItem.url, chunkBlob, partNumber);
          state.completedParts.push({ part_number: partNumber, etag });
        } catch (err) {
          // Back in front of the buffer, so the next pass or the final part still carries it.
          bufferRef.current.unshift(chunkBlob);
          bufferSizeRef.current += chunkBlob.size;
          throw err;
        }

        fetchMoreUrlsIfNeeded();
      }
    } catch (err) {
      logger.warn('[Recording] chunk upload failed; it stays buffered for the next pass', err);
      if (!isNetworkFailure(err)) toast.error({ id: 'recording-chunk-upload', title: CHUNK_UPLOAD_FAILED_MESSAGE });
    } finally {
      isUploadingRef.current = false;
    }
  }, [
    adapter,
    uploadChunkWithRetry,
    fetchMoreUrlsIfNeeded,
    uploadStateRef,
    isUploadingRef,
    bufferSizeRef,
    bufferRef,
  ]);

  // ── Shared: auto-stop + performStop ──────────────────────────────────

  const performStopMediaRecorder = useCallback(
    async (scopeId: string, failure?: CaptureFailure) => {
      const recorder = mediaRecorderRef.current;
      const state = uploadStateRef.current;

      if (!recorder || !state) {
        reportStopFailed(RECORDING_STOP_FAILED_MESSAGE);
        return;
      }

      clearAutoStopTimer();
      updateStatus('completing');
      setIsProcessing(true);

      const inputSwaps = liveInputRef.current?.swapEvents() ?? [];
      const db = new RecordingDB(config.idbConfig);
      try {
        // Resolves after the final dataavailable has landed in bufferRef.
        await stopMediaRecorder(recorder);
        while (isUploadingRef.current) await new Promise((r) => setTimeout(r, 100));
        // Into IndexedDB before any upload, so an offline stop keeps the tail for a retry.
        await db.open();
        await persistFallbackParts(db, scopeId, state, bufferRef.current);
        bufferRef.current = [];
        bufferSizeRef.current = 0;
      } catch (err) {
        db.close();
        reportStopFailed(err instanceof Error ? err.message : RECORDING_STOP_FAILED_MESSAGE);
        return;
      } finally {
        releaseStream();
        mediaRecorderRef.current = null;
      }
      const queue = new ChunkUploadQueue(db, scopeId, state.uploadId, (payload) => adapter.moreUrls(payload));
      await finishFromDb(scopeId, state, db, queue, inputSwaps, failure);
    },
    [
      updateStatus,
      releaseStream,
      clearAutoStopTimer,
      finishFromDb,
      reportStopFailed,
      setIsProcessing,
      adapter,
      config.idbConfig,
      mediaRecorderRef,
      uploadStateRef,
      liveInputRef,
      isUploadingRef,
      bufferRef,
      bufferSizeRef,
    ],
  );

  const performStop = useCallback(
    async (scopeId: string, failure?: CaptureFailure) => {
      if (isStoppingRef.current) return;
      isStoppingRef.current = true;
      stopAtTimeRef.current = 0;
      try {
        if (engineTypeRef.current === 'audio-worklet') {
          await performStopWorklet(scopeId, failure);
        } else {
          await performStopMediaRecorder(scopeId, failure);
        }
      } finally {
        isStoppingRef.current = false;
      }
    },
    [performStopWorklet, performStopMediaRecorder, isStoppingRef, stopAtTimeRef, engineTypeRef],
  );

  const triggerAutoStop = useCallback(
    (scopeId: string) => {
      if (statusRef.current !== 'recording' && statusRef.current !== 'paused') return;
      autoStoppedRef.current = true;
      setWasAutoStopped(true);
      void performStop(scopeId)
        .then(() => {
          setActiveScopeId(null);
        })
        .catch(() => {});
    },
    [performStop, statusRef, autoStoppedRef, setWasAutoStopped, setActiveScopeId],
  );

  /** Silence or an encoder error ends the take like a user stop: final part, drain, complete. Never abort. */
  const stopOnCaptureFailure = useCallback(
    (scopeId: string, failure: CaptureFailure) => {
      if ((statusRef.current !== 'recording' && statusRef.current !== 'paused') || isStoppingRef.current) return;
      logger.warn(`[Recording] capture ${failure}; stopping and keeping the audio`);
      void performStop(scopeId, failure)
        .then(() => setActiveScopeId(null))
        .catch(() => {});
    },
    [performStop, statusRef, isStoppingRef, setActiveScopeId],
  );
  useEffect(() => {
    captureFailureRef.current = stopOnCaptureFailure;
  }, [stopOnCaptureFailure]);

  const scheduleAutoStop = useCallback(
    (durationMs: number, scopeId: string) => {
      clearAutoStopTimer();

      stopAtTimeRef.current = Date.now() + durationMs;

      autoStopTimerRef.current = setTimeout(() => {
        triggerAutoStop(scopeId);
      }, durationMs);
    },
    [clearAutoStopTimer, triggerAutoStop, stopAtTimeRef, autoStopTimerRef],
  );

  // ── start ─────────────────────────────────────────────────────────────────

  const start = useCallback(
    async (scopeId: string, filename: string, options: RecordingStartOptions = {}) => {
      logger.log('[Recording] start() invoked', {
        scopeId,
        status: statusRef.current,
        activeScopeId,
      });

      if (statusRef.current !== 'idle' && statusRef.current !== 'error') {
        logger.log(
          `[Recording] start() bailed early — status is "${statusRef.current}" (must be idle/error)`,
        );
        return;
      }

      // Dual-recording guard — prevent recording two scopes simultaneously
      if (activeScopeId && activeScopeId !== scopeId) {
        logger.log(
          `[Recording] start() bailed early — already recording for scope ${activeScopeId}`,
        );
        toast.error('Already recording for another session');
        return;
      }

      setError(null);
      autoStoppedRef.current = false;
      isStoppingRef.current = false;
      stopAtTimeRef.current = 0;
      setWasAutoStopped(false);
      updateStatus('starting');
      setActiveScopeId(scopeId);

      // Register the auto-stop trigger BEFORE starting so the worklet chunk
      // handler can call it as soon as the wall clock is reached.
      triggerAutoStopRef.current = () => triggerAutoStop(scopeId);

      let fellBackFrom: MicChoice | null = null;
      try {
        const useWorklet = AudioWorkletRecorder.isSupported();
        const contentType = 'audio/mp4';

        logger.log(
          `[Recording] start() — engine: ${useWorklet ? 'AudioWorklet+MediaRecorder hybrid' : 'MediaRecorder fallback'}, scopeId: ${scopeId}`,
        );

        const response = await adapter.start({ filename, content_type: contentType });

        logger.log(
          `[Recording] start API response — upload_id: ${response.upload_id}, part_size_bytes: ${response.part_size_bytes}, presigned_urls: ${response.presigned_urls.length}, max_duration_minutes: ${response.max_duration_minutes ?? 'null (using config)'}`,
        );

        const maxMinutes = response.max_duration_minutes ?? config.maxRecordingMinutes;
        maxDurationMsRef.current = maxMinutes * 60 * 1000;
        segmentStartTimeRef.current = Date.now();

        uploadStateRef.current = {
          uploadId: response.upload_id,
          objectKey: response.object_key,
          partSizeBytes: response.part_size_bytes,
          presignedUrls: [...response.presigned_urls],
          nextPartNumber: response.presigned_urls.length + 1,
          completedParts: [],
          recordingStartTime: Date.now(),
        };

        if (useWorklet) {
          engineTypeRef.current = 'audio-worklet';

          wavAccRef.current = [];
          wavAccSizeRef.current = 0;
          s3PartNumberRef.current = 1;
          pendingChunkRef.current = Promise.resolve();

          const db = new RecordingDB(config.idbConfig);
          await db.open();
          recordingDbRef.current = db;
          logger.log('[Recording] IndexedDB opened');

          uploadQueueRef.current = new ChunkUploadQueue(
            db,
            scopeId,
            response.upload_id,
            (payload) => adapter.moreUrls(payload),
            (err) => {
              // The part stays in IndexedDB as `failed`; the next pass (or coming back online) retries it.
              logger.error('[Recording] background chunk upload failed:', err);
              if (!isNetworkFailure(err)) {
                toast.error({ id: `recording-upload-${scopeId}`, title: CHUNK_UPLOAD_FAILED_MESSAGE });
              }
            },
          );

          const worklet = new AudioWorkletRecorder();
          workletRecorderRef.current = worklet;
          const opened = await worklet.start((blob) => handleWorkletChunk(blob, scopeId), {
            mic: options.mic,
            onInputEnded: handleInputEnded,
            onError: (failure) => captureFailureRef.current(scopeId, failure),
          });
          liveInputRef.current = worklet.getInput();
          fellBackFrom = opened.fellBackFrom;
          flushTailRef.current = (final) => flushTail(scopeId, final);
          logger.log(`[Recording] AudioWorkletRecorder started — mimeType: ${worklet.getMimeType()}`);
        } else {
          engineTypeRef.current = 'media-recorder';

          if (typeof MediaRecorder === 'undefined') {
            throw new Error('Your browser does not support audio recording');
          }

          const mimeType = getSupportedMimeType();
          if (!mimeType) {
            throw new Error('No supported audio format found in your browser');
          }

          bufferRef.current = [];
          bufferSizeRef.current = 0;

          const opened = await openMic(options.mic, { fallback: true });
          fellBackFrom = opened.fellBackFrom;
          let recordStream = opened.stream;
          const graph = await startFallbackGraph().catch(() => null);
          if (graph) {
            inputContextRef.current = graph;
            const destination = graph.createMediaStreamDestination();
            destination.channelCount = 1;
            destination.channelCountMode = 'explicit';
            const input = new LiveInput(graph, destination, handleInputEnded);
            try {
              await input.attach(opened);
            } catch (err) {
              stopStream(opened.stream);
              throw err;
            }
            liveInputRef.current = input;
            recordStream = destination.stream;
          } else {
            streamRef.current = opened.stream;
          }

          const recorder = new MediaRecorder(recordStream, { mimeType });
          mediaRecorderRef.current = recorder;

          recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              bufferRef.current.push(event.data);
              bufferSizeRef.current += event.data.size;
              void processBuffer();
            }
          };

          recorder.onerror = () => captureFailureRef.current(scopeId, 'error');
          // A suspended graph or a muted / ended mic records nothing: the same stall.
          watchRecorderData(
            recorder,
            () => captureFailureRef.current(scopeId, 'stalled'),
            () =>
              inputContextRef.current
                ? inputContextRef.current.state !== 'running' || !liveInputRef.current?.delivering
                : !streamDelivering(streamRef.current),
          );

          recorder.start(1000);
        }

        updateStatus('recording');
        scheduleAutoStop(maxDurationMsRef.current, scopeId);
        const mic = liveInputRef.current?.mic;
        if (mic) emitInput({ statusRef, liveInputRef, inputListenersRef }, { type: 'opened', mic, fellBackFrom });
      } catch (err) {
        // Diagnostic log — surfaces WHY the recording API / mic init failed
        if (axios.isAxiosError(err)) {
          const res = err.response;
          const reason = res
            ? `HTTP ${res.status} ${res.statusText}`
            : `network error (${err.code ?? 'no response'} — backend down, CORS, or unreachable)`;
          const url = `${err.config?.baseURL ?? ''}${err.config?.url ?? ''}`;
          const backend = res?.data ? JSON.stringify(res.data) : err.message;
          logger.error(
            `[Recording] start FAILED — scopeId: ${scopeId} | reason: ${reason} | url: ${url} | detail: ${backend}`,
            err,
          );
        } else {
          logger.error(
            `[Recording] start FAILED — scopeId: ${scopeId} | ${
              err instanceof Error ? `${err.name}: ${err.message}` : String(err)
            }`,
            err,
          );
        }

        const message =
          err instanceof DOMException && err.name === 'NotAllowedError'
            ? 'Microphone permission denied. Please allow microphone access to record.'
            : err instanceof Error
              ? err.message
              : 'Failed to start recording';
        setError(message);
        updateStatus('error');
        toast.error(message);

        // Cleanup partial initialisation
        workletRecorderRef.current?.destroy();
        workletRecorderRef.current = null;
        liveInputRef.current = null;
        recordingDbRef.current?.close();
        recordingDbRef.current = null;
        releaseStream();
        setActiveScopeId(null);
      }
    },
    [
      activeScopeId,
      adapter,
      config,
      updateStatus,
      processBuffer,
      releaseStream,
      clearAutoStopTimer,
      scheduleAutoStop,
      handleWorkletChunk,
      triggerAutoStop,
      setError,
      setWasAutoStopped,
      setActiveScopeId,
      statusRef,
      autoStoppedRef,
      isStoppingRef,
      stopAtTimeRef,
      maxDurationMsRef,
      segmentStartTimeRef,
      uploadStateRef,
      engineTypeRef,
      wavAccRef,
      wavAccSizeRef,
      s3PartNumberRef,
      pendingChunkRef,
      recordingDbRef,
      uploadQueueRef,
      workletRecorderRef,
      bufferRef,
      bufferSizeRef,
      streamRef,
      mediaRecorderRef,
      triggerAutoStopRef,
      handleInputEnded,
      flushTail,
      flushTailRef,
      liveInputRef,
      inputContextRef,
      inputListenersRef,
    ],
  );

  // ── pause ─────────────────────────────────────────────────────────────────

  const pause = useCallback(() => {
    if (statusRef.current !== 'recording') return;

    const elapsed = Date.now() - segmentStartTimeRef.current;
    remainingMsRef.current = Math.max(0, maxDurationMsRef.current - elapsed);
    clearAutoStopTimer();

    if (engineTypeRef.current === 'audio-worklet') {
      workletRecorderRef.current?.pause();
    } else {
      mediaRecorderRef.current?.pause();
    }

    updateStatus('paused');
  }, [
    updateStatus,
    clearAutoStopTimer,
    statusRef,
    segmentStartTimeRef,
    maxDurationMsRef,
    remainingMsRef,
    engineTypeRef,
    workletRecorderRef,
    mediaRecorderRef,
  ]);

  // ── resume ────────────────────────────────────────────────────────────────

  const resume = useCallback(() => {
    if (statusRef.current !== 'paused') return;

    if (engineTypeRef.current === 'audio-worklet') {
      workletRecorderRef.current?.resume();
    } else {
      mediaRecorderRef.current?.resume();
    }

    updateStatus('recording');

    if (remainingMsRef.current > 0) {
      const currentScopeId = ctx.activeScopeId;
      segmentStartTimeRef.current = Date.now();
      maxDurationMsRef.current = remainingMsRef.current;
      if (currentScopeId) scheduleAutoStop(remainingMsRef.current, currentScopeId);
    }
  }, [
    ctx.activeScopeId,
    updateStatus,
    scheduleAutoStop,
    statusRef,
    engineTypeRef,
    workletRecorderRef,
    mediaRecorderRef,
    remainingMsRef,
    segmentStartTimeRef,
    maxDurationMsRef,
  ]);

  // ── stop ──────────────────────────────────────────────────────────────────

  const stop = useCallback(async () => {
    if (statusRef.current !== 'recording' && statusRef.current !== 'paused') return;
    const currentScopeId = ctx.activeScopeId;
    if (!currentScopeId) {
      reportStopFailed(RECORDING_STOP_FAILED_MESSAGE);
      return;
    }
    await performStop(currentScopeId);
    setActiveScopeId(null);
  }, [performStop, reportStopFailed, setActiveScopeId, statusRef, ctx.activeScopeId]);

  // ── abort ─────────────────────────────────────────────────────────────────

  const abort = useCallback(async () => {
    clearAutoStopTimer();
    stopAtTimeRef.current = 0;
    isStoppingRef.current = false;
    flushTailRef.current = null;

    if (engineTypeRef.current === 'audio-worklet') {
      workletRecorderRef.current?.destroy();
      workletRecorderRef.current = null;
      liveInputRef.current = null;
      uploadQueueRef.current?.cancel();
      uploadQueueRef.current = null;
      wavAccRef.current = [];
      wavAccSizeRef.current = 0;

      const db = recordingDbRef.current;
      const currentScopeId = ctx.activeScopeId;
      if (db && currentScopeId) {
        await db.clearRecording(currentScopeId).catch(() => {});
        db.close();
        recordingDbRef.current = null;
      }
    } else {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      releaseStream();
      mediaRecorderRef.current = null;
      bufferRef.current = [];
      bufferSizeRef.current = 0;
    }

    const state = uploadStateRef.current;
    if (state?.uploadId) {
      await adapter.abort({ upload_id: state.uploadId }).catch(() => {});
    }

    uploadStateRef.current = null;
    engineTypeRef.current = null;
    updateStatus('idle');
    setError(null);
    setActiveScopeId(null);
  }, [
    updateStatus,
    releaseStream,
    clearAutoStopTimer,
    setError,
    setActiveScopeId,
    adapter,
    ctx.activeScopeId,
    stopAtTimeRef,
    isStoppingRef,
    engineTypeRef,
    workletRecorderRef,
    uploadQueueRef,
    wavAccRef,
    wavAccSizeRef,
    recordingDbRef,
    mediaRecorderRef,
    bufferRef,
    bufferSizeRef,
    uploadStateRef,
    liveInputRef,
    flushTailRef,
  ]);

  // ── halt ──────────────────────────────────────────────────────────────────

  const halt = useCallback(async () => {
    if (statusRef.current !== 'recording' && statusRef.current !== 'paused') return;
    const scopeId = ctx.activeScopeId;
    clearAutoStopTimer();
    stopAtTimeRef.current = 0;
    flushTailRef.current = null;
    isStoppingRef.current = true;
    try {
      if (engineTypeRef.current === 'audio-worklet') {
        const worklet = workletRecorderRef.current;
        await worklet?.stop();
        worklet?.destroy();
        workletRecorderRef.current = null;
        liveInputRef.current = null;
        await pendingChunkRef.current;
        if (scopeId) await persistAccumulated(scopeId);
        uploadQueueRef.current?.cancel();
        uploadQueueRef.current = null;
        recordingDbRef.current?.close();
        recordingDbRef.current = null;
      } else {
        const recorder = mediaRecorderRef.current;
        const state = uploadStateRef.current;
        if (recorder) await stopMediaRecorder(recorder);
        if (state && scopeId) {
          const db = new RecordingDB(config.idbConfig);
          await db.open();
          await persistFallbackParts(db, scopeId, state, bufferRef.current).finally(() => db.close());
          bufferRef.current = [];
          bufferSizeRef.current = 0;
        }
        releaseStream();
        mediaRecorderRef.current = null;
      }
    } catch (err) {
      logger.error('[Recording] halt could not save the last audio', err);
    } finally {
      isStoppingRef.current = false;
    }
    uploadStateRef.current = null;
    engineTypeRef.current = null;
    updateStatus('idle');
    setActiveScopeId(null);
  }, [
    ctx.activeScopeId,
    config.idbConfig,
    clearAutoStopTimer,
    persistAccumulated,
    releaseStream,
    setActiveScopeId,
    updateStatus,
    statusRef,
    stopAtTimeRef,
    flushTailRef,
    isStoppingRef,
    engineTypeRef,
    workletRecorderRef,
    liveInputRef,
    pendingChunkRef,
    uploadQueueRef,
    recordingDbRef,
    mediaRecorderRef,
    uploadStateRef,
    bufferRef,
    bufferSizeRef,
  ]);

  // ── Mic input ───────────────────────────────────────────────────────────

  const switchMic = useCallback(
    (choice: MicChoice | null, reason: MicSwitchReason = 'user') =>
      switchEngineInput({ statusRef, liveInputRef, inputListenersRef }, choice, reason),
    [statusRef, liveInputRef, inputListenersRef],
  );
  const getActiveMic = useCallback(() => liveInputRef.current?.mic ?? null, [liveInputRef]);
  const getAnalyser = useCallback(() => liveInputRef.current?.analyser ?? null, [liveInputRef]);
  const getStream = useCallback(
    () => liveInputRef.current?.stream ?? streamRef.current ?? null,
    [liveInputRef, streamRef],
  );

  // ── Upload an existing recording ───────────────────────────────────────

  /**
   * The alternative to live capture: the manager already has the audio.
   *
   * It reuses the live path's status transitions rather than inventing its own
   * so that `isProcessing`, the recording chip and the result poll need no
   * knowledge of where the bytes came from. There is no mic, no worklet and no
   * auto-stop clock here — just the multipart lifecycle.
   */
  const uploadFile = useCallback(
    async (
      scopeId: string,
      file: File,
      onProgress?: (progress: FileUploadProgress) => void,
    ) => {
      if (statusRef.current !== 'idle' && statusRef.current !== 'error') {
        logger.log(
          `[Recording] uploadFile() bailed — status is "${statusRef.current}" (must be idle/error)`,
        );
        return;
      }
      if (activeScopeId && activeScopeId !== scopeId) {
        toast.error('Already recording for another session');
        return;
      }

      setError(null);
      setWasAutoStopped(false);
      updateStatus('uploading');
      setActiveScopeId(scopeId);

      try {
        const result = await uploadRecordingFile(adapter, file, { onProgress });

        updateStatus('completing');
        // Mirrors completeUpload: the 202 means the AI work has STARTED, so
        // processing stays true until the poll or WS reports a terminal status.
        // Clearing it here would flash the processing UI off and read as done.
        setIsProcessing(true);
        if (result.job_id != null) setActiveJobId(result.job_id);
        updateStatus('completed');
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Could not upload that recording.';
        logger.error('[Recording] uploadFile failed:', error);
        setError(message);
        updateStatus('error');
        setIsProcessing(false);
        setActiveScopeId(null);
        toast.error(message);
      }
    },
    [
      adapter,
      activeScopeId,
      statusRef,
      setError,
      setWasAutoStopped,
      updateStatus,
      setActiveScopeId,
      setIsProcessing,
      setActiveJobId,
    ],
  );

  // Keep the auto-stop trigger ref in sync with the latest callback.
  useEffect(() => {
    // The ref is scoped at start() time with the scopeId, but the callback
    // itself can be stale from the effect closure. This re-assignment ensures
    // the latest performStop is used when the timer fires.
    if (ctx.activeScopeId) {
      triggerAutoStopRef.current = () => triggerAutoStop(ctx.activeScopeId!);
    }
  }, [triggerAutoStop, ctx.activeScopeId, triggerAutoStopRef]);

  // NOTE: beforeunload and visibilitychange handlers are owned by RecordingProvider
  // (they survive component unmounts). No unmount cleanup here.

  return {
    start,
    switchMic,
    getActiveMic,
    getAnalyser,
    getStream,
    stop,
    halt,
    pause,
    resume,
    abort,
    uploadFile,
    status,
    isProcessing,
  };
}
